import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/Layout";
import { initials, fmtDate } from "@/lib/format";
import { ArrowLeft, Shield, KeySquare, Users2, UserMinus, Cloud, Trash2, Copy } from "lucide-react";

const ATTR_FIELDS = [
  ["displayName", "field.displayName"], ["userPrincipalName", "field.upn"],
  ["jobTitle", "field.jobTitle"], ["department", "field.department"],
  ["companyName", "field.company"], ["officeLocation", "field.officeNumber"],
  ["mobilePhone", "field.mobile"], ["managerName", "field.manager"],
  ["streetAddress", "field.street"], ["postalCode", "field.zip"],
  ["city", "field.city"], ["usageLocation", "field.usageLocation2"],
  ["employeeId", "field.employeeId"],
];

const Chip = ({ children }) => (
  <span className="inline-flex max-w-full items-center gap-1 border border-zinc-200 bg-zinc-50 px-2 py-1 text-xs font-medium text-zinc-700">
    <span className="truncate">{children}</span>
  </span>
);

function Section({ icon: Icon, title, count, items, render, empty, testid }) {
  return (
    <div className="border border-zinc-200 bg-white" data-testid={testid}>
      <div className="flex items-center justify-between border-b border-zinc-200 bg-zinc-50 px-5 py-3">
        <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-600"><Icon size={14} /> {title}</h2>
        <span className="font-mono text-xs text-zinc-400">{count}</span>
      </div>
      <div className="p-5">
        {items.length === 0 ? <p className="text-sm text-zinc-400">{empty}</p>
          : <div className="flex flex-wrap gap-1.5">{items.map(render)}</div>}
      </div>
    </div>
  );
}

export default function LeavedDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const [u, setU] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/leaved-employees/${id}`).then((r) => setU(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err) return (<><PageHeader title={t("leaved.title")} /><div className="p-10 text-sm text-zinc-400">{t("leaved.notFound")}</div></>);
  if (!u) return (<><PageHeader title={t("leaved.title")} /><div className="p-10 font-mono text-xs uppercase tracking-widest text-zinc-400">{t("common.loading")}</div></>);

  const groups = u.resolvedGroups || [];
  const licenses = u.resolvedLicenses || [];
  const teams = u.resolvedTeams || [];

  return (
    <>
      <PageHeader title={t("leaved.title")} subtitle={t("leaved.subtitle")} />
      <div className="space-y-8 p-6 md:p-10">
        <Link to="/employees?filter=leaved" data-testid="leaved-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
          <ArrowLeft size={15} /> {t("leaved.backToList")}
        </Link>

        {/* Header card */}
        <div className="flex flex-col gap-4 border border-zinc-200 bg-white p-6 sm:flex-row sm:items-center" data-testid="leaved-header">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center bg-zinc-900 text-lg font-bold text-white">{initials(u.displayName)}</span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-display text-xl font-bold tracking-tight text-zinc-950">{u.displayName}</h2>
              <span data-testid="leaved-badge" className="inline-flex items-center gap-1 border border-violet-200 bg-violet-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-800"><UserMinus size={11} /> {t("leaved.badge")}</span>
              {u.isCloudOnly && <span className="inline-flex items-center gap-1 border border-sky-300 bg-sky-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-sky-700"><Cloud size={11} /> {t("emp.cloudOnlyBadge")}</span>}
              {u.deletedFromTenant && <span data-testid="leaved-deleted-badge" className="inline-flex items-center gap-1 border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-700"><Trash2 size={11} /> {t("leaved.deletedTag")}</span>}
            </div>
            <div className="mt-0.5 font-mono text-sm text-zinc-500">{u.userPrincipalName}</div>
          </div>
          <Link to={`/onboard?clone=${u.id}`} data-testid="leaved-clone-link" className="inline-flex h-10 shrink-0 items-center gap-2 border border-zinc-300 bg-white px-4 text-sm font-semibold uppercase tracking-wide text-zinc-700 transition-colors hover:bg-zinc-50">
            <Copy size={15} /> {t("leaved.cloneToNew")}
          </Link>
        </div>

        {/* Offboard metadata */}
        <div className="grid grid-cols-2 gap-px bg-zinc-200 sm:grid-cols-4" data-testid="leaved-meta">
          {[[t("leaved.leftOn"), u.offboardedOn || "—"], [t("leaved.leaveDate"), u.leaveDate || "—"],
            [t("leaved.offboardedBy"), u.offboardedBy || "—"], [t("leaved.snapshotAt"), u.snapshotAt ? fmtDate(u.snapshotAt) : "—"]].map(([lbl, val]) => (
            <div key={lbl} className="bg-white p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{lbl}</div>
              <div className="mt-1 truncate text-sm font-semibold text-zinc-800">{val}</div>
            </div>
          ))}
        </div>

        {/* Attributes */}
        <div className="border border-zinc-200 bg-white" data-testid="leaved-attributes">
          <div className="border-b border-zinc-200 bg-zinc-50 px-5 py-3">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-600">{t("leaved.attributes")}</h2>
          </div>
          <div className="grid grid-cols-1 gap-x-8 gap-y-4 p-5 sm:grid-cols-2 lg:grid-cols-3">
            {ATTR_FIELDS.map(([k, label]) => (
              <div key={k}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{t(label)}</div>
                <div className="mt-0.5 text-sm text-zinc-800">{u[k] || "—"}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Permissions & resources */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <Section icon={Shield} title={t("leaved.permissions")} count={groups.length} items={groups} testid="leaved-groups"
            empty={t("leaved.noneGroups")} render={(g) => <Chip key={g.id}>{g.displayName}</Chip>} />
          <Section icon={KeySquare} title={t("leaved.licenses")} count={licenses.length} items={licenses} testid="leaved-licenses"
            empty={t("leaved.noneLicenses")} render={(l) => <Chip key={l.skuId}>{l.name}</Chip>} />
          <Section icon={Users2} title={t("leaved.teams")} count={teams.length} items={teams} testid="leaved-teams"
            empty={t("leaved.noneTeams")} render={(tm) => <Chip key={tm.id}>{tm.displayName}</Chip>} />
        </div>
      </div>
    </>
  );
}
